/**
 * ASK FOR THE COUNTER AT SIGN-IN — AND WAIT FOR THE ANSWER.
 *
 * WHY THIS EXISTS. The counter is the whole billing attribution — cash sessions
 * were removed on 2026-08-18, so (counter, collector, business date) is what
 * every collection groups by. But the counter was a PERSISTED BROWSER CHOICE
 * with no default: a cashier signing in on a machine they had not billed from
 * before had none set, and every collection surface refused them with "select a
 * counter" on a screen that has no counter picker (the consultation fee dialog,
 * IP advance). The answer exists at sign-in; nothing was asking for it.
 *
 * IT IS ASKED, NOT ASSUMED — UNLESS THE HOSPITAL HAS SAID. Inventing a counter
 * by picking the first one in the branch would post a cashier's money to
 * whichever desk happened to sort first, a mistake nobody notices until the day
 * is reconciled. So a GUESS is still refused.
 *
 * What is no longer refused is an ANSWER. `Role.defaultCounterId` (2026-09-07)
 * lets the hospital state, per role, which desk that role stands at, and
 * `GET /op-billing/assigned-counter` resolves it from a foreign key — not from a
 * role NAME, which is tenant data anybody can rename. Where an assignment
 * exists this gate does not ask; where it does not, nothing has changed.
 *
 * AN ASSIGNED COUNTER BYPASSES `isSelectableDesk`. That fence keeps a
 * `MAIN_COUNTER` out of the PICKER, because a main counter is an aggregation
 * node real desks report into and offering it in a list would let a cashier
 * attribute the day's takings to the roll-up. An assigned counter was not chosen
 * from a list — and this tenant's own "Main Cash Counter" IS typed
 * `MAIN_COUNTER`, which is exactly why nobody could ever select it.
 *
 * ONLY FOR PEOPLE WHO BILL. A doctor, a lab technician or an administrator
 * signing in has no cash desk, and demanding one before they can reach their own
 * work would be a modal wall in front of the wrong people. Gated on
 * `financial:payment:process` — the permission that means "this person takes
 * money".
 *
 * IT BLOCKS. This reverses the original "dismissible, deliberately" decision, at
 * the hospital's explicit request: the desk must name where it is standing
 * before any screen opens, because a bill taken from an unattributed desk is one
 * nobody can reconcile at the end of the day. `AppLayout` renders this INSTEAD OF
 * the application — not over it — so there is nothing behind the dialog to
 * click at. The one thing that still lets the app through is a branch with no
 * selectable desk: an unanswerable question must not become a locked door, and
 * the billing screens carry their own message for that case.
 */
import { useEffect, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Dialog, Select, Text } from '@components/ui-tw';
import { usePermission } from '@hooks/usePermission';
import { useAuthStore } from '@store/auth.store';
import { useBranchStore } from '@store/branch.store';
import { useBillingCounterStore } from '@store/billing-counter.store';
import { FinancialPermissions, type CashCounterDto } from '@nexao/contracts';
import { listCashCounters } from '@features/finance/services/cash-counter.service';
import { useAssignedCounter } from '@features/op-billing/hooks/useAssignedCounter';

/**
 * A desk someone can actually stand at.
 *
 * A `MAIN_COUNTER` is excluded because it is an aggregation node, not a
 * position: `counterRequiresParent()` in the contracts package is the same
 * distinction from the other side — every real desk hangs off one. Offering it
 * would let a cashier attribute the day's takings to the roll-up its own
 * children report into.
 *
 * A `PHARMACY` counter is excluded too (owner, 2026-10-31: it is "for pharmacy only"): it is picked on the Pharmacy screen, where its takings are settled to the
 * Main Counter. Offered here, an OP cashier could bill a consultation into the pharmacy's register, and the pharmacy's settlement would carry it.
 */
export function isSelectableDesk(counter: CashCounterDto): boolean {
  return counter.isActive && counter.counterType !== 'MAIN_COUNTER' && counter.counterType !== 'PHARMACY';
}

export interface CounterSelection {
  /** True while the operator still owes an answer — `AppLayout` shows nothing else until this clears. */
  required: boolean;
  loading: boolean;
  options: CashCounterDto[];
  choose: (counterId: string) => void;
  /**
   * True when the hospital has assigned this person's desk, so nothing should
   * offer them a different one. `BillingCounterSelector` renders fixed text
   * instead of a dropdown.
   */
  assigned: boolean;
}

/**
 * The single source of the "does this person still owe us a counter" rule,
 * shared by the dialog below and by `AppLayout`'s decision to render it instead
 * of the app. Both call it; React Query dedupes the fetch by key, so asking
 * twice costs one request.
 */
export function useCounterSelection(): CounterSelection {
  const takesMoney = usePermission(FinancialPermissions.FINANCIAL_PAYMENT_PROCESS);

  /**
   * ADMINISTRATORS ARE NOT ASKED — desk, 2026-09-06: "remove counter selection
   * for admin logins".
   *
   * This file's own header already said the gate is "only for people who bill"
   * and named an administrator as someone it should not stop. The check did not
   * match the words: it gates on `financial:payment:process`, and an
   * administrator holds EVERY permission, so the one person the rule meant to
   * exempt was the one it always caught — a modal wall in front of every admin
   * session, on screens like the IP dashboard that take no money at all.
   *
   * `ADMINISTRATION` is the module claim, not a role NAME: role names are
   * tenant data and a renamed role would silently re-lock the door, while the
   * module is the same claim the sidebar and the workspace switcher already
   * gate on, so all three agree by construction. It currently resolves to
   * SuperAdmin and System Administrator, and nothing else.
   *
   * ── WHY THIS CANNOT LET UNATTRIBUTED MONEY THROUGH ──────────────────────
   *
   * The gate is a CONVENIENCE that front-loads the question, not the control
   * that enforces the answer. An administrator who genuinely takes a payment is
   * still refused by the SERVER — V1b, "select a counter before billing" — and
   * every billing surface carries `BillingCounterSelector` to answer it there.
   * So the attribution guarantee is unchanged; only the modal wall in front of
   * people who were never going to bill is gone.
   */
  const isAdministrator = useAuthStore((state) => state.moduleCodes.includes('ADMINISTRATION'));
  const mayBill = takesMoney && !isAdministrator;
  const branch = useBranchStore((state) => state.currentBranch);
  const currentCounter = useBillingCounterStore((state) => state.currentCounter);
  const setCounter = useBillingCounterStore((state) => state.setCounter);

  /*
    THE HOSPITAL'S OWN ANSWER, ASKED FOR BEFORE THE OPERATOR IS.

    Fetched for every biller rather than only those without a counter, because
    an assignment must also OVERRIDE a stale persisted choice: a cashier who
    picked OPD on this machine last week and has since been assigned to Main
    Cash must end up on Main Cash, not on whatever localStorage remembers.
  */
  const assigned = useAssignedCounter(mayBill);

  const counters = useQuery({
    queryKey: ['cash-counters', 'gate', branch?.id ?? null],
    queryFn: () => listCashCounters(1, 100, branch?.id),
    // Fetched for every biller, not only those without a counter: a counter
    // ALREADY held has to be checked against this list too (see below). A
    // doctor or lab technician still issues no request at all.
    enabled: mayBill && Boolean(branch?.id),
    staleTime: 5 * 60_000,
  });

  const options = (counters.data?.items ?? []).filter(isSelectableDesk);
  const loaded = !counters.isPending && counters.isSuccess;
  const heldIsSelectable = Boolean(currentCounter && options.some((option) => option.id === currentCounter.id));

  /**
   * A COUNTER THAT IS NO LONGER A DESK IS NOT AN ANSWER.
   *
   * The choice is persisted in `localStorage`, so it outlives the counter
   * master: a desk that has since been deactivated, retyped as a MAIN_COUNTER,
   * or removed from this branch stays pinned in the browser and silently
   * suppresses this prompt — the operator keeps billing to a desk that no
   * longer exists, and nobody finds out until the day is reconciled. Dropping
   * it re-asks, which is the whole point of the gate.
   */
  useEffect(() => {
    if (!loaded || !currentCounter) return;
    /*
      AN ASSIGNED COUNTER IS EXEMPT, and it has to be. `isSelectableDesk`
      excludes a `MAIN_COUNTER` from `options`, and this tenant's assigned desk
      IS one — so without this guard the assignment would be written by the
      effect above, immediately cleared here for not being in `options`, and
      written again: an infinite loop that also re-opens the very dialog the
      assignment exists to suppress.
    */
    if (assigned.counter && currentCounter.id === assigned.counter.id) return;
    if (!heldIsSelectable) setCounter(null);
  }, [loaded, currentCounter, heldIsSelectable, setCounter, assigned.counter]);

  /**
   * AN ASSIGNMENT IS APPLIED, NOT OFFERED.
   *
   * Written straight into the store, so every billing surface — the context
   * bar, the consultation fee dialog, the IP advance screen — reads the same
   * counter without any of them knowing an assignment exists.
   *
   * IT OVERWRITES A DIFFERENT HELD COUNTER rather than filling only when empty
   * (which is what `BillingCounterSelector` does for an open cash session, and
   * is right there). The two cases differ: an open session is evidence of where
   * the cashier IS, and a deliberate switch to cover another desk must survive.
   * A role assignment is the hospital saying where they BELONG, and the desk
   * asked that these users "access only main cash counter" — so a leftover
   * choice from another machine or another posting does not win.
   *
   * The equality check is what stops this being an infinite render loop: it
   * writes only when the held counter is not already the assigned one.
   */
  useEffect(() => {
    const target = assigned.counter;
    if (!mayBill || !target || assigned.ambiguous) return;
    if (currentCounter?.id === target.id) return;
    setCounter({ id: target.id, code: target.code, name: target.name, branchId: target.branchId });
  }, [assigned.counter, assigned.ambiguous, mayBill, currentCounter?.id, setCounter]);

  /**
   * WHOSE COUNTER IS DECIDED FOR THEM IS NEVER ASKED.
   *
   * `isLoading` holds the question back rather than letting it flash: asking
   * "which counter are you at?" for a moment and then answering it for them is
   * a worse experience than a beat of nothing, and on a slow connection the
   * operator would have already started choosing.
   *
   * AMBIGUOUS IS NOT ASSIGNED. Two roles naming two desks has no correct answer,
   * so those operators are asked exactly as before — the server refuses to flip
   * a coin and so does this.
   */
  const decidedForThem = Boolean(assigned.counter) && !assigned.ambiguous;

  /**
   * `assigned.isLoading` IS NOT PART OF THIS, and that was a real defect for one
   * release.
   *
   * It was, briefly: `mustAsk && … && !assigned.isLoading`. That made `required`
   * FALSE while the assignment call was in flight — and `required` is what
   * `AppLayout` renders the gate INSTEAD of the application on. So a cashier on a
   * slow connection was shown the whole app, unasked, with no counter set, and
   * could start billing in that window. Unattributed takings are precisely what
   * this gate exists to prevent, so "we do not know yet" must HOLD, never pass.
   *
   * Not knowing is handled below, in `required`, by treating an unresolved
   * assignment the same way an unresolved counter LIST has always been treated:
   * hold the app and show the dialog's own loading state.
   */
  const mustAsk =
    mayBill &&
    Boolean(branch?.id) &&
    !decidedForThem &&
    (!currentCounter || (loaded && !heldIsSelectable));

  /**
   * A STALE COUNTER FROM ANOTHER BRANCH IS NOT A CHOICE.
   *
   * `BillingCounterSelector` already clears one that does not belong to the
   * current branch. This mirrors it so the gate re-asks after a branch switch
   * rather than leaving the operator silently attributed to a desk in a
   * building they are no longer in.
   */
  useEffect(() => {
    if (!currentCounter || !branch?.id) return;
    // Exempt for the same reason as above: clearing an assigned counter only
    // starts the write/clear loop again.
    if (assigned.counter && currentCounter.id === assigned.counter.id) return;
    if (currentCounter.branchId && currentCounter.branchId !== branch.id) setCounter(null);
  }, [currentCounter, branch?.id, setCounter, assigned.counter]);

  function choose(counterId: string): void {
    const picked = options.find((counter) => counter.id === counterId);
    if (!picked) return;
    setCounter({ id: picked.id, code: picked.code, name: picked.name, branchId: picked.branchId ?? null });
  }

  /**
   * EITHER unanswered question holds the app: which desks exist, and which one
   * this person is assigned to. Both are needed before the gate can decide
   * whether to ask at all, and passing on a half-loaded answer is how somebody
   * bills unattributed.
   */
  const stillResolving = counters.isPending || assigned.isLoading;

  return {
    // While either answer is still loading we DO hold the app, but only for a
    // biller who has no counter at all: flashing the application and then
    // throwing a blocking dialog over it is worse than waiting. Someone who
    // already holds one keeps working while the check runs, and is only
    // interrupted if it comes back saying their desk is gone. A branch with no
    // selectable desk resolves to `false` — see the header on why that must not
    // lock anyone out.
    required: mustAsk && ((stillResolving && !currentCounter) || options.length > 0),
    loading: stillResolving,
    options,
    choose,
    assigned: decidedForThem,
  };
}

export function CounterSelectionGate(): JSX.Element | null {
  const { required, loading, options, choose } = useCounterSelection();
  const [chosen, setChosen] = useState('');

  if (!required) return null;

  return (
    <Dialog open onClose={() => undefined} title="Which counter are you at?" size="sm">
      <div className="tw:flex tw:min-w-[20rem] tw:flex-col tw:gap-3">
        <Text variant="caption" tone="muted">
          Every bill and collection you take is attributed to this desk. You can change it later from the billing
          screen.
        </Text>

        {loading ? (
          <Text variant="caption" tone="muted">
            Loading counters…
          </Text>
        ) : (
          <>
            <Select
              value={chosen}
              onChange={(event) => setChosen(event.target.value)}
              placeholder="Select a counter…"
              options={options.map((counter) => ({ value: counter.id, label: `${counter.name} (${counter.code})` }))}
              aria-label="Counter"
            />

            <Button type="button" disabled={!chosen} onClick={() => choose(chosen)}>
              Use this counter
            </Button>
          </>
        )}
      </div>
    </Dialog>
  );
}
